import { memo, useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import { CurrencyProvider } from 'contexts/currencyContext'
import ModalContext from 'contexts/ModalContext'
import JourneyV2DetailsModal from 'components/Flights/V2/FareFamily/JourneyV2DetailsModal'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import DrawerFlightCard from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/FlightSearchDrawer/DrawerFlightCard'
import { getFlightOptionJourneyKey, optionToJourney } from './chatFlightTypes'
import type { FlightRecommendationItem, FlightRecommendationsUIData, FlightOption } from './chatFlightTypes'
import useFlightRecommendationBooking from './useFlightRecommendationBooking'

interface Props {
  data: FlightRecommendationsUIData
}

const RecommendationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  width: 100%;
`

const RecommendationShell = styled.div<{ $isUnavailable: boolean }>`
  opacity: ${props => props.$isUnavailable ? 0.58 : 1};
`

const ReturnRecommendationShell = styled(Sheet)<{ $isUnavailable: boolean }>`
  opacity: ${props => props.$isUnavailable ? 0.58 : 1};
  padding: ${rem(12)};
`

const PairGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const PairFooter = styled.div`
  align-items: center;
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  display: flex;
  gap: ${rem(12)};
  justify-content: space-between;
  padding-top: ${rem(12)};
`

const UnavailablePill = styled(BodyText)`
  align-self: flex-start;
  background: ${props => props.theme.palette.neutral.default.six};
  border-radius: ${rem(999)};
  padding: ${rem(4)} ${rem(8)};
`

function getJourneyKey(option: FlightOption): string | undefined {
  return getFlightOptionJourneyKey(option)
}

function getRecommendationKey(item: FlightRecommendationItem, index: number): string {
  const outboundKey = getJourneyKey(item.outbound) ??
    `${item.outbound.leg.flightNumber}-${item.outbound.leg.departureDate}-${item.outbound.leg.departureTime}-${item.outbound.leg.arrivalAirport}`
  const inboundKey = item.inbound ?
    getJourneyKey(item.inbound) ??
    `${item.inbound.leg.flightNumber}-${item.inbound.leg.departureDate}-${item.inbound.leg.departureTime}-${item.inbound.leg.arrivalAirport}` :
    'one-way'

  return `${outboundKey}-${inboundKey}-${index}`
}

function ChatFlightRecommendationCard({
  item,
  index,
}: {
  item: FlightRecommendationItem
  index: number
}) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { handleSelect, isSubmitting, isUnavailable } = useFlightRecommendationBooking(
    item,
    index,
    showModal,
  )

  const outboundJourney = useMemo(
    () => optionToJourney(item.outbound, `flight-recommendation-${index}-outbound`, item.currencyCode),
    [index, item.currencyCode, item.outbound],
  )

  const inboundJourney = useMemo(
    () => item.inbound
      ? optionToJourney(item.inbound, `flight-recommendation-${index}-inbound`, item.currencyCode)
      : undefined,
    [index, item.currencyCode, item.inbound],
  )

  const selectLabel = isSubmitting
    ? intl.formatMessage({
        id: 'HMjUm43uODPuyWsRQCwj',
        defaultMessage: 'Checking...',
        description: 'Button label while validating a flight recommendation before booking',
      })
    : intl.formatMessage({
        id: 'KufjvH807sVS6HGbiMBs',
        defaultMessage: 'Select',
        description: 'Button label for selecting a recommended flight from chat',
      })

  const unavailableLabel = intl.formatMessage({
    id: '1HnVD7gkX-v6L2lWH0ms',
    defaultMessage: 'Fare no longer available',
    description: 'Status label shown on a stale flight recommendation tile',
  })

  const handleOutboundDetails = useCallback(() => {
    showModal(<JourneyV2DetailsModal journey={outboundJourney} />)
  }, [outboundJourney, showModal])

  const handleInboundDetails = useCallback(() => {
    if (!inboundJourney) return
    showModal(<JourneyV2DetailsModal journey={inboundJourney} />)
  }, [inboundJourney, showModal])

  if (!inboundJourney) {
    return (
      <RecommendationShell $isUnavailable={isUnavailable}>
        <Group direction="vertical" gap={8}>
          {isUnavailable && <UnavailablePill variant="small" colour="neutral-two">{unavailableLabel}</UnavailablePill>}
          <DrawerFlightCard
            journey={outboundJourney}
            onSelect={handleSelect}
            onViewDetails={handleOutboundDetails}
            isSelectDisabled={isSubmitting || isUnavailable}
            selectLabel={selectLabel}
            alignColumns
            responsiveCompact
          />
        </Group>
      </RecommendationShell>
    )
  }

  return (
    <ReturnRecommendationShell border="neutral-five" borderRadius="L" $isUnavailable={isUnavailable}>
      <PairGrid>
        {isUnavailable && <UnavailablePill variant="small" colour="neutral-two">{unavailableLabel}</UnavailablePill>}
        <BodyText variant="medium" weight="bold" colour="neutral-two">
          {intl.formatMessage({
            id: 'IQ3f1jGdw73Qp38g_sUn',
            defaultMessage: 'Departing flight',
            description: 'Label for the outbound leg in a return flight recommendation',
          })}
        </BodyText>
        <DrawerFlightCard
          journey={outboundJourney}
          onViewDetails={handleOutboundDetails}
          showSelectButton={false}
          showPrice={false}
          alignColumns
          responsiveCompact
        />
        <BodyText variant="medium" weight="bold" colour="neutral-two">
          {intl.formatMessage({
            id: 'm0vOQQ2JazCwANddE4sc',
            defaultMessage: 'Returning flight',
            description: 'Label for the inbound leg in a return flight recommendation',
          })}
        </BodyText>
        <DrawerFlightCard
          journey={inboundJourney}
          onViewDetails={handleInboundDetails}
          showSelectButton={false}
          showPrice={false}
          alignColumns
          responsiveCompact
        />
        <PairFooter>
          <CurrencyProvider currency={item.currencyCode}>
            <PriceRowPrice size="S" price={item.pricePerPerson} saleUnit="adult" />
          </CurrencyProvider>
          <TextButton kind="secondary" size="small" onClick={handleSelect} disabled={isSubmitting || isUnavailable}>
            {selectLabel}
          </TextButton>
        </PairFooter>
      </PairGrid>
    </ReturnRecommendationShell>
  )
}

function ChatFlightRecommendations({ data }: Props) {
  if (data.items.length === 0) {
    return null
  }

  return (
    <RecommendationsList>
      {data.items.map((item, index) => (
        <ChatFlightRecommendationCard
          key={getRecommendationKey(item, index)}
          item={item}
          index={index}
        />
      ))}
    </RecommendationsList>
  )
}

export default memo(ChatFlightRecommendations)
